import "ui/assets/fonts/font-awesome.min.css";
import "ui/assets/fonts/Icomoon/mobile/style.css";
import "ui/assets/fonts/Icomoon/desktop/style.css";
import "prismjs/themes/prism-tomorrow.css";
import "ui/assets/scss/styles.scss";
import "ui/assets/css/react-mde-all.css";
import "ui/styles/tailwind.css";
import LogRocket from "logrocket";
import React from "react";
import { Localization } from "react-widgets";
import MomentLocalizer from "react-widgets-moment";
import { Link } from "next/link";
import { trpc } from "trpc/client";
import App from "next/app";
import i18n from "i18next";
import Head from "next/head";
import { configure } from "mobx";
import { Provider } from "mobx-react";
import moment from "moment";
import TurndownService from "turndown";
import Cookies from "universal-cookie";
import NextAdapterPages from "next-query-params/pages";
import config, { clientConfig, serverConfig } from "../env/config";
import { dehydrate, hydrate, HydrationBoundary } from "@tanstack/react-query";
import { trpc as bareTrpc } from "trpc/bare";
import Agent from "../env/agent";
import MobxApp from "ui/_pages/MobxApp";
import initializeStore from "env/stores/stores";
import { DefaultHead } from "ui/components/DefaultHead";
import { whiteLabelCss } from "ui/assets/css/whitelabel";
import YesNoModalDialog from "ui/components/YesNoModalDialog";
import PopUpMessageModal from "ui/components/PopUpMessageModal";
import { analyticsService } from "env/analytics/analyticsService";
import { handleServerRequest, isServer } from "env/ssr/ServerSideRenderManager";
import { emptyAuthTokens } from "../env/stores/authTokens/authTokens";
import { defaultAuthTokensFactory } from "../env/stores/authTokens/defaultAuthTokensFactory";
import { ServerCookies, WriteableServerCookies } from "../env/utils/serverCookies";
import TokenRefresher from "./TokenRefresher";
import { withSSR } from "react-i18next";
import { AppInsightsProvider } from "../AppInsightsProvider";
import Script from "next/script";
import { meQueryOptions } from "../ui/hooks/meQuery";
import { FetchHelper } from "../env/stores/authTokens/FetchHelper";
import { NexudusLastActiveProvider } from "../env/context/nexudusLastActiveContext";
import { TRPCProvider } from "../trpc/client";
import { getQueryClient } from "../env/getQueryClient";
import { getQueryKey } from "@trpc/react-query";
import { QueryParamProvider } from "use-query-params";

export const NEXUDUS_PUBLIC_DOMAIN = '.spaces.nexudus.com';
export const NEXUDUS_PUBLIC_DOMAIN_STAGING = '.spacesstaging.nexudus.com';
export const NEXUDUS_PUBLIC_DOMAIN_DEMO = '.spaces-demo.nexudus.com';

export const VERCEL_PUBLIC_DOMAIN = '.nexudus.site';
export const VERCEL_PUBLIC_DOMAIN_STAGING = '.staging.nexudus.site';
export const VERCEL_PUBLIC_DOMAIN_DEMO = 'kalkiodemo-';
export const DEFAULT_NEXUDUS_DOMAIN = 'kalkiospace.spaces.nexudus.com';

const NodeCache = require('node-cache');
const cache = new NodeCache({ stdTTL: 60 * 60 * 24 });
const cookies = new Cookies();
const _clientConfig = clientConfig();

// Log rocket init
LogRocket.init(_clientConfig.logRocketId, {
  release: _clientConfig.releaseId,
});

//Disable markdown escaping in Turndown.
TurndownService.prototype.escape = (x) => x;

//Fix for full calendar ssr
if (typeof Element === 'undefined') {
  global.Element = function () {};
}

if (!isServer()) {
  require('env/i18n');
  window.$ = window.jQuery = require('jquery');
  require('bootstrap/dist/js/bootstrap.bundle.js');
  // configureAnchors({ offset: -100, scrollDuration: 200 });
}

configure({
  enforceActions: 'observed', //strict mode
});

//react components localiser
const momentLocalizer = new MomentLocalizer(moment);
const fetchHelper = new FetchHelper();

const IGNORED_PATHS_FOR_AUTH = /.*(ico|js|json|map)/;
class CustomApp extends App {
  tokensStore = emptyAuthTokens; // The default value here shouldn't ever be used but we have it on the server before the first request

  // This method runs on the server when loading any page
  // and on the client when navigating between pages.
  // It creates the stores and serializes its properties in the server
  // to achieve Server Side Rendering and then pass
  // store data on to the client so stores can be hydrated as needed.
  static async getInitialProps(appContext) {
    let requestHost = appContext.ctx.req && appContext.ctx.req.headers.host;
    if (!requestHost) {
      // Happens on client only
      return await App.getInitialProps(appContext);
    }

    const serverCookies = ServerCookies.parse(
      appContext.ctx.req?.headers.cookie || '',
    );
    const tokensStore = defaultAuthTokensFactory.createOnServer(serverCookies);

    const queryClient = getQueryClient();
    const config = serverConfig();
    // Calculate host for public API.
    const host =
      // Demo hosting
      requestHost.indexOf(VERCEL_PUBLIC_DOMAIN_DEMO) > -1
        ? `${
            requestHost.split(VERCEL_PUBLIC_DOMAIN)[0]
          }${NEXUDUS_PUBLIC_DOMAIN_DEMO}`
        : // Vercel staging
          requestHost.indexOf(VERCEL_PUBLIC_DOMAIN_STAGING) > -1
          ? `${
              requestHost.split(VERCEL_PUBLIC_DOMAIN_STAGING)[0]
            }${NEXUDUS_PUBLIC_DOMAIN_STAGING}`
          : // Vercel production
            requestHost.indexOf(VERCEL_PUBLIC_DOMAIN) > -1
            ? `${
                requestHost.split(VERCEL_PUBLIC_DOMAIN)[0]
              }${NEXUDUS_PUBLIC_DOMAIN}`
            : // Nexudus hosting
              requestHost.indexOf(NEXUDUS_PUBLIC_DOMAIN) > -1
              ? `${
                  requestHost.split(NEXUDUS_PUBLIC_DOMAIN)[0]
                }${NEXUDUS_PUBLIC_DOMAIN}`
              : // custom hosting
                config.nexudusEnvRoot
                ? config.nexudusEnvRoot.replace('https://', '') //Pass-in domain
                : requestHost.indexOf('vercel') == -1 &&
                    requestHost?.indexOf('localhost') == -1 //staging and dev vercel domains.
                  ? ((
                      await new Agent(
                        config.platformRoot.replace('https://', ''),
                        tokensStore,
                      ).requests.get(
                        `/api/sys/businesses/getByHost?host=${requestHost}`,
                        '',
                      )
                    ).WebAddress ?? requestHost) //Custom domains
                  : // demo site
                    `${DEFAULT_NEXUDUS_DOMAIN}`;

    const path = appContext.ctx.req.url;
    const pathIsIgnored = IGNORED_PATHS_FOR_AUTH.test(path);

    if (!pathIsIgnored) {
      await tokensStore.ensureAllTokensAreReady();
    }

    const mobxStore = initializeStore({ host, authTokens: tokensStore });

    if (!mobxStore.appStore.hasLoadedConfiguration) {
      await mobxStore.appStore.loadConfiguration();
    }

    if (!mobxStore.appStore.hasLoadedBusiness)
      await mobxStore.appStore.loadBusiness();

    let me = null;
    if (tokensStore.isLoggedIn && !mobxStore.authStore.hasLoadedMe) {
      await mobxStore.authStore.loadMe().catch(() => null);
    }
    if (tokensStore.isLoggedIn && !mobxStore.authStore.hasLoadedCustomer) {
      await mobxStore.authStore.loadCustomer().catch(() => null);
    }

    if (mobxStore.authStore.customer && me) {
      const options = meQueryOptions(
        mobxStore.authStore.agent,
        mobxStore.authStore.customer?.Email,
      );

      queryClient.setQueryData(options.queryKey, me);
    }

    await handleServerRequest({ appContext, mobxStore });

    analyticsService.pageView({ location: appContext.ctx.asPath });

    const appProps = await App.getInitialProps(appContext);

    if (appContext.ctx.res) {
      const writeableCookies = new WriteableServerCookies(
        new ServerCookies({}),
      );
      tokensStore.saveCookies(writeableCookies);

      const cookieStrings = writeableCookies.createCookiesStrings();

      if (cookieStrings.length > 0) {
        appContext.ctx.res.setHeader('Set-Cookie', cookieStrings);
      }
    }

    // Preload the feature flags since every page should have them available immediately
    try {
      queryClient.setQueryData(
        getQueryKey(trpc.config.getFeatureFlags, undefined, 'query'),
        await bareTrpc.config.getFeatureFlags.query(undefined),
      );
    } catch (e) {
      console.warn('Error preloading feature flags', e);
    }

    return {
      ...appProps,
      mobxStore: mobxStore,
      dehydratedState: dehydrate(queryClient),
      tokens: tokensStore.serialized,
      appInsightsConnectionString: config.applicationInsightsConnectionString,
    };
  }

  constructor(props) {
    super(props);

    if (props.tokens) {
      this.tokensStore = defaultAuthTokensFactory.createOnClient(props.tokens);
    } else {
      // Shouldn't ever happen!
      console.warn('No tokens passed to the app constructor');
      this.tokensStore = emptyAuthTokens;
    }

    this.mobxStore = isServer()
      ? props.mobxStore
      : initializeStore({
          stores: props.mobxStore,
          authTokens: this.tokensStore,
        });

    const queryClient = getQueryClient();

    if (props.dehydratedState) {
      hydrate(queryClient, props.dehydratedState);
    }
    this.state = {
      babelReady: isServer(),
      queryClient: queryClient,
    };
  }

  async componentDidMount() {
    // Add Babel standalone
    // We load Babel here and not in Head so we can
    // attach the load event handler. Babel must be loaded before
    // any components are rendered in case they have been customized
    // and need to be transformed by babel
    const config = clientConfig();

    if (!isServer()) {
      document.version = {
        package: _clientConfig.packageVersion,
        release: _clientConfig.releaseId,
      };

      LogRocket.getSessionURL((sessionUrl) => {
        console.log({ sessionUrl });
      });
      const isLoginPage =
        document.location.pathname.toLowerCase().indexOf('/login') != -1;
      if (!isLoginPage && this.mobxStore.authStore.accessToken) {
        const user = await this.mobxStore.authStore.loadMe();
        if (user) {
          //Load customer details and identify logrocket user
          await this.mobxStore.authStore.loadCustomer();
          const { customer } = this.mobxStore.authStore;
          LogRocket.identify(String(customer.Id), {
            name: customer?.FullName,
            email: customer?.Email,
            HomeLocation: customer?.HomeSpaceName,
          });

          //Load customer home location details
          const homeLocationURL = this.mobxStore.authStore.customer?.Url;
          const homeLocationHostName = new URL(homeLocationURL).hostname;

          await this.mobxStore.homeStore.loadConfiguration(
            homeLocationHostName,
          );

          await this.mobxStore.homeStore.configureHost(homeLocationHostName);
          await this.mobxStore.communityStore.configureHost(
            homeLocationHostName,
          );
          await this.mobxStore.supportStore.configureHost(homeLocationHostName);

          if (!this.mobxStore.homeStore.hasLoadedBusiness) {
            await this.mobxStore.homeStore.loadBusiness();
          }

          await this.mobxStore.bookingsStore.configureHomeLocationHost(
            homeLocationHostName,
          );

          // Push user properties to GTM
          if (typeof window !== 'undefined') {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: 'userInfoAvailable', // custom event name
              HomeLocation: customer?.HomeSpaceName || 'Unknown',
              CompanyName: customer?.TeamNames || 'Unknown',
            });
          }

          //Set current language
          await this.mobxStore.authStore.selectLanguage(i18n.language ?? 'en');

          //SignalR scripts
          const jquerySignalR = document.createElement('script');
          jquerySignalR.src = `/js/jquery.signalR-2.4.3.min.js`;

          jquerySignalR.addEventListener('load', () => {
            const signalHubs = document.createElement('script');
            signalHubs.src = `${config.platformRoot}/signalr/hubs`;

            signalHubs.addEventListener('load', () => {
              const signalR = document.createElement('script');
              signalR.src = '/js/signalr.js?v=6';
              signalR.addEventListener('load', () => {
                const getAccessToken = () => {
                  return this.mobxStore.authStore.loadMe().then((user) => user);
                };
                var manager = new PushNotificationsManager(getAccessToken);
                manager.initPushNotifications(config.platformRoot);
              });
              document.body.appendChild(signalR);
            });

            document.body.appendChild(signalHubs);
          });

          document.body.appendChild(jquerySignalR);
        }
      }

      const script = document.createElement('script');
      script.src = 'https://unpkg.com/@babel/standalone@7.10.2/babel.min.js';
      script.addEventListener('load', () => {
        this.setState({ babelReady: true });
      });
      document.body.appendChild(script);

      window.nexudus = this.mobxStore;

      if (window.app) window.app();
      if (window.componentDidMount) window.componentDidMount();

      if (window.parent !== window) {
        document.documentElement.classList.add('inside-iframe');
      }

      if (
        cookies.get('in-ms-teams') == 'true' ||
        window.location.search?.indexOf('in-ms-teams=true') > -1
      ) {
        document.documentElement.classList.add('hide-main-menu');
        document.documentElement.classList.add('in-ms-teams');
        cookies.set('in-ms-teams', 'true');
      }

      if (window.location.search?.indexOf('hide-main-menu=true') > -1) {
        document.documentElement.classList.add('hide-main-menu');
      }

      this.mobxStore.bookingsStore.loadUnpaidBookings().catch(() => null);

      this.mobxStore.appStore
        .loadServerMessages()
        .then((data) => {
          const message =
            data.ErrorMessage ??
            data.SuccessMessage ??
            data.Message ??
            data.WarningMessage;
          if (message) {
            this.mobxStore.appStore.setPopMessage(t(message));
          }
        })
        .catch(() => {});
    }
  }

  determinePrimaryColor() {
    const { appStore } = this.mobxStore;
    const { configuration } = appStore;
    const { authStore } = this.mobxStore;

    let primaryColor = configuration['PrimaryWebColor'];

    if (
      authStore?.isLoggedIn &&
      authStore?.customer?.HomeSpaceId == clientConfig().renoldBusinessId
    ) {
      primaryColor = config.renoldThemeColour;
    }

    return primaryColor;
  }

  render() {
    const { Component, pageProps } = this.props;

    const { appStore } = this.mobxStore;
    const { configuration } = appStore;
    const primaryColor = this.determinePrimaryColor();
    const customHead = eval(configuration['Website.CustomHead']) ?? [];
    if (!isServer()) {
      // Google tag manager and GA4
      analyticsService.initAnalytics(_clientConfig.ga4, _clientConfig.gtag);

      document.documentElement.lang = i18n.language?.toLowerCase() || 'en';
      const rtl = ['ar', 'he'].indexOf(document.documentElement.lang) > -1;
      document.body.dir = rtl ? 'rtl' : 'ltr';
      if (rtl) {
        customHead.push({
          type: 'link',
          rel: 'stylesheet',
          href: '//cdn.rawgit.com/morteza/bootstrap-rtl/v3.3.4/dist/css/bootstrap-rtl.min.css',
        });
      }
    }

    const head = (
      <Head>
        <style type="text/css">
          {whiteLabelCss({ configuration, primaryColor, appStore })}
        </style>
        {analyticsService.gtmHead(_clientConfig.gtag)}
      </Head>
    );

    const script = (
      <React.Fragment>
        <Script
          id="recapthca"
          async
          defer
          src="https://www.google.com/recaptcha/api.js"
        />
        <Script
          id="stripe"
          defer
          async
          type="text/javascript"
          src="https://js.stripe.com/v3/"
        />
        <Script
          id="fpe"
          defer
          async
          src="https://code.archilogic.com/fpe-sdk/v3.1.x/fpe.js"
        />
        <Script
          id="frameresizer"
          defer
          type="text/javascript"
          src="/js/iframeResizer.contentWindow.min.js"
        />
      </React.Fragment>
    );

    return (
      <Provider {...this.mobxStore}>

        <QueryParamProvider adapter={NextAdapterPages}>
          <TRPCProvider queryClient={this.state.queryClient}>
          <HydrationBoundary state={this.props.dehydratedState}>
            <AppInsightsProvider
              appInsightsConnectionString={
                this.props.appInsightsConnectionString
              }
            >
              <DefaultHead appStore={this.mobxStore.appStore} />
              {head}
              {script}

              <MobxApp>
                <NexudusLastActiveProvider>
                    {analyticsService.gtmNoHead(_clientConfig.gtag)}
                    <PopUpMessageModal />
                    <YesNoModalDialog />
                    <TokenRefresher />
                    <Localization date={momentLocalizer}>
                      <div id="react-modal"></div>
                      <Component {...pageProps} />
                    </Localization>
                </NexudusLastActiveProvider>
              </MobxApp>
            </AppInsightsProvider>
          </HydrationBoundary>
        </TRPCProvider>
        </QueryParamProvider>
      </Provider>
    );
  }
}

export default withSSR()(CustomApp);
